import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Alert,
  Tooltip,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import GridViewIcon from '@mui/icons-material/GridView';
import QosMatrixModal from './QosMatrixModal';

interface DatasetUploadProps {
  disabled?: boolean;
  onFileSelect?: (file: File, content: string) => void;
  onError?: (message: string) => void;
  taskCount?: number;
  seed?: number;
  onSeedChange?: (seed: number) => void;
  taskSpecificVariationEnabled?: boolean;
  onTaskSpecificVariationChange?: (enabled: boolean) => void;
}

const DatasetUpload: React.FC<DatasetUploadProps> = ({
  disabled = false,
  onFileSelect,
  onError,
  taskCount = 1,
  seed,
  onSeedChange,
  taskSpecificVariationEnabled = true,
  onTaskSpecificVariationChange,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [datasetText, setDatasetText] = useState('');
  const [matrixModalOpen, setMatrixModalOpen] = useState(false);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      
      // Read file content
      try {
        const content = await file.text();
        setDatasetText(content);
        if (onFileSelect) {
          onFileSelect(file, content);
        }
      } catch (error) {
        console.error('Error reading file:', error);
        if (onError) {
          onError('Error reading file: ' + (error instanceof Error ? error.message : String(error)));
        }
      }
    }
  };

  return (
    <Box
      component="fieldset"
      disabled={disabled}
      aria-disabled={disabled}
      sx={{
        border: 0,
        p: 0,
        m: 0,
        minWidth: 0,
        ...(disabled
          ? {
              opacity: 0.6,
              pointerEvents: 'none',
            }
          : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h2">
          Dataset
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload QWS dataset file (CSV format)
        </Typography>

        <input
          accept=".csv,.txt"
          style={{ display: 'none' }}
          id="dataset-upload-input"
          type="file"
          disabled={disabled}
          onChange={handleFileSelect}
        />
        <Box sx={{ display: 'flex', gap: 1.25, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Tooltip title="Choose the QWS dataset file used to build the task-service matrix for optimization." arrow placement="top">
            <label htmlFor="dataset-upload-input" style={{ flex: 1 }}>
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<CloudUploadIcon />}
                disabled={disabled}
              >
                Browse Dataset
              </Button>
            </label>
          </Tooltip>

          <Tooltip
            title="Open the deterministic QoS matrix preview and inspect the active matrix seed."
            arrow
            placement="top"
          >
            <span style={{ display: 'flex', flex: 1 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<GridViewIcon />}
                disabled={disabled || !datasetText}
                onClick={() => setMatrixModalOpen(true)}
              >
                QoS Matrix
              </Button>
            </span>
          </Tooltip>
        </Box>

        <Tooltip
          title="When enabled, each task gets a deterministic QoS variation based on the active seed. Disable it to use the raw QWS values for every task."
          arrow
          placement="top"
        >
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={taskSpecificVariationEnabled}
                  onChange={(event) => onTaskSpecificVariationChange?.(event.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label="Task-specific QoS variation"
              sx={{ ml: 0 }}
            />
          </Box>
        </Tooltip>

        {selectedFile && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" component="span">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </Typography>
              {seed !== undefined && (
                <Chip
                  label={`Matrix Seed ${seed}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              <Chip
                label={taskSpecificVariationEnabled ? 'Variation On' : 'Variation Off'}
                size="small"
                color={taskSpecificVariationEnabled ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>
          </Alert>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary">
        Expected format: ResponseTime, Availability, Throughput, Successability,
        Reliability, Compliance, BestPractices, Latency, Documentation, ServiceName, WSDL_URL
      </Typography>

      <QosMatrixModal
        open={matrixModalOpen}
        onClose={() => setMatrixModalOpen(false)}
        datasetText={datasetText}
        taskCount={taskCount}
        seed={seed}
        onSeedChange={onSeedChange}
        taskSpecificVariationEnabled={taskSpecificVariationEnabled}
        onTaskSpecificVariationChange={onTaskSpecificVariationChange}
        selectedFileName={selectedFile?.name}
      />
    </Box>
  );
};

export default DatasetUpload;
